import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Box, Button, TextField } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectDistributorDetail } from 'src/store/selectors/distributorSelectors';
import DropdownMenu from 'src/components/DropdownMenu/DropdownMenu';
import {
  getInitialPaymentList,
  getTermList
} from 'src/utils/constants/distributors';
import Autocomplete from 'src/components/Autocomplete/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'src/utils/dayjs';
import { formatPositiveAmount } from 'src/utils/formatAmout';
import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from '../../initialState';

const StepOne = ({ data, handleNext, isQuoteFromRentCalculator }) => {
  const { classes } = useStyles();
  const initialState = makeInitialState(data);
  const { t } = useTranslation();
  const { minInitialPayment, minTerm, maxTerm } = useSelector(
    selectDistributorDetail
  );

  const onNext = useCallback(
    async values => {
      handleNext({ ...data, ...values });
    },
    [handleNext]
  );

  return (
    <>
      <Formik
        initialValues={initialState}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onNext}
      >
        {({
          errors,
          handleSubmit,
          touched,
          values,
          setFieldValue,
          handleChange,
          handleBlur
        }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Box className={classes.container}>
                <Autocomplete
                  label={t('CONTRACT_VIEW.INITIAL_PAYMENT_PERCENTAGE')}
                  name="initialPayment"
                  options={getInitialPaymentList(minInitialPayment)}
                  id="initialPayment"
                  freeSolo
                  value={
                    isQuoteFromRentCalculator
                      ? formatPositiveAmount(values.initialPayment)
                      : values.initialPayment
                  }
                  disabled={isQuoteFromRentCalculator}
                  clearOnBlur={false}
                  fullWidth
                  sx={{ marginBottom: 1, marginTop: 2 }}
                  clearIcon={null}
                  inputType="number"
                  error={Boolean(
                    touched.initialPayment && errors.initialPayment
                  )}
                  helperText={
                    touched.initialPayment && t(errors.initialPayment)
                  }
                  onInputChange={(event, newValue) => {
                    setFieldValue('initialPayment', Number(newValue));
                    setFieldValue(
                      'initialPaymentAmount',
                      (newValue / 100) * values.price
                    );
                  }}
                />

                <DropdownMenu
                  onBlur={handleBlur}
                  label={t('COMMON.TERM')}
                  value={values.term}
                  disabled={isQuoteFromRentCalculator}
                  name="term"
                  setValue={value => setFieldValue('term', value)}
                  error={Boolean(touched.term && errors.term)}
                  errorText={touched.term && t(errors.term)}
                  list={getTermList(minTerm, maxTerm)}
                />

                <DatePicker
                  label={t('CONTRACT_VIEW.INITIAL_PAYMENT_DATE')}
                  format="DD/MM/YYYY"
                  value={values.initialPaymentDate}
                  minDate={dayjs.tz()}
                  onChange={newValue => {
                    setFieldValue('initialPaymentDate', dayjs(newValue));
                  }}
                  slotProps={{
                    textField: {
                      sx: { width: '100%', mt: 2, mb: 1 },
                      error: Boolean(touched.initialPaymentDate && errors.initialPaymentDate),
                      helperText: touched.initialPaymentDate && t(errors.initialPaymentDate),
                      FormHelperTextProps: { sx: { color: 'red' } },
                      InputProps: { autoComplete: 'off' }
                    }
                  }}
                />

                <DatePicker
                  label={t('CONTRACT_VIEW.FIRST_RENT_DATE')}
                  format="DD/MM/YYYY"
                  value={values.firstRentDate}
                  minDate={
                    values.initialPaymentDate
                      ? values.initialPaymentDate
                      : dayjs.tz()
                  }
                  onChange={newValue => {
                    setFieldValue('firstRentDate', dayjs(newValue));
                  }}
                  slotProps={{
                    textField: {
                      sx: { width: '100%', mt: 2, mb: 1 },
                      error: Boolean(touched.firstRentDate && errors.firstRentDate),
                      helperText: touched.firstRentDate && t(errors.firstRentDate),
                      FormHelperTextProps: { sx: { color: 'red' } },
                      InputProps: { autoComplete: 'off' }
                    }
                  }}
                />

                <TextField
                  error={Boolean(touched.moreInfo && errors.moreInfo)}
                  fullWidth
                  helperText={touched.moreInfo && t(errors.moreInfo)}
                  label={t('COMMON.MORE_EQUIPMENT_INFO')}
                  name="moreInfo"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  multiline
                  margin="normal"
                  type="text"
                  maxRows={4}
                  value={values.moreInfo}
                  variant="outlined"
                  inputProps={{ maxLength: 120, autoComplete: 'off' }}
                />
              </Box>
              <Box
                className={classes.box}
                display="flex"
                justifyContent="flex-end"
              >
                <Button
                  className={clsx(classes.buttons, classes.buttonMain)}
                  type="submit"
                  color="primary"
                  variant="outlined"
                >
                  {t('COMMON.NEXT')}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>
    </>
  );
};
StepOne.propTypes = {
  data: PropTypes.object,
  handleNext: PropTypes.func,
  isQuoteFromRentCalculator: PropTypes.bool
};

export default StepOne;
